import * as React from "react";
import GooglePayButton from "@google-pay/button-react";
import GooglePayMetadata from "../../../../models/googlePayMetadata";
import { ErrorMessage } from "../../../../viewModels/common/types";
import * as Sentry from "@sentry/react";
import { useState } from "react";
import Alert from "react-bootstrap/Alert";

type EDAZGooglePayButtonProps = {
  loading: boolean;
  googlePayMetadata: GooglePayMetadata | undefined;
  locale: string;
  createOrder?:
    | ((
        errorMessage: ErrorMessage,
        paymentData?: google.payments.api.PaymentData,
        turnstileToken?: string | null
      ) => Promise<void>)
    | ((errorMessage: ErrorMessage, paymentData?: google.payments.api.PaymentData) => void);
  validateOrder: () => boolean;
  errorMessage: string;
  totalPrice: number | undefined;
  fillContainer?: boolean;
};

export const EDAZGooglePayButton = ({
  googlePayMetadata,
  locale,
  loading,
  createOrder,
  validateOrder,
  errorMessage,
  totalPrice,
  fillContainer,
}: EDAZGooglePayButtonProps) => {
  const [error, setError] = useState(false);

  const paymentRequest: google.payments.api.PaymentDataRequest | undefined = googlePayMetadata
    ? {
        apiVersion: googlePayMetadata.apiVersion,
        apiVersionMinor: googlePayMetadata.apiVersionMinor,
        merchantInfo: {
          merchantId: googlePayMetadata.googlePayMerchantId,
          merchantName: googlePayMetadata.merchantName,
        },
        allowedPaymentMethods: [
          {
            type: googlePayMetadata.paymentMethodType,
            parameters: {
              allowedAuthMethods: googlePayMetadata.allowedCardAuthMethods,
              allowedCardNetworks: googlePayMetadata.allowedCardNetworks,
            },
            tokenizationSpecification: {
              type: googlePayMetadata.tokenizationSpecificationType as "PAYMENT_GATEWAY",
              parameters: {
                gateway: googlePayMetadata.gateway,
                gatewayMerchantId: googlePayMetadata.gatewayMerchantId,
              },
            },
          },
        ],
        transactionInfo: {
          totalPriceStatus: "FINAL",
          totalPriceLabel: "Total",
          totalPrice: (totalPrice ?? 0).toString(),
          currencyCode: "CZK",
          countryCode: "CZ",
        },
      }
    : undefined;

  const onClick = async (event: Event) => {
    if (!validateOrder()) {
      event.preventDefault();

      if (createOrder) {
        await createOrder("invalidData");
      }
    }
  };

  const onLoadPaymentData = async (paymentRequest: google.payments.api.PaymentData) => {
    if (createOrder) {
      await createOrder(undefined, paymentRequest);
    }
  };

  const onError = (error: Error | google.payments.api.PaymentsError) => {
    setError(true);

    Sentry.captureException(error);
  };

  return (
    <>
      {loading ? (
        <div className="spinner-border spinner-border-sm" role="status">
          <span className="sr-only">Loading...</span>
        </div>
      ) : (
        <>
          {googlePayMetadata && paymentRequest && (
            <GooglePayButton
              environment={googlePayMetadata.environment}
              paymentRequest={paymentRequest}
              onLoadPaymentData={onLoadPaymentData}
              onClick={onClick}
              onError={onError}
              buttonLocale={locale}
              {...(fillContainer
                ? {
                    buttonSizeMode: "fill" as const,
                    style: { width: "100%", height: "100%" },
                  }
                : {})}
            />
          )}
        </>
      )}
    </>
  );
};
